import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { appActions } from "../../../ducks/app";
import {
  AppliedCoupon,
  couponActions,
  CouponExternal,
  CouponGroupExternal,
  couponSelectors,
  CouponType,
  MenuExternalCoupon,
} from "../../../ducks/coupon";
import { RedirectPickupInfo, useCoupon } from "../../../hooks/useCoupon";
import { useNavigation } from "../../../hooks/useNavigation";

export const useAppCouponList = () => {
  const dispatch = useDispatch();
  const { navigateToPage } = useNavigation();

  const [selectedCouponGroup, setSelectedCouponGroup] =
    useState<CouponGroupExternal | null>(null);

  const couponExternalGroups = useSelector(
    couponSelectors.couponExternalGroups,
  );
  const remainingSlot = useSelector(couponSelectors.remainingSlot);
  const {
    applyCoupon,
    appliedCoupons,
    selectedAppCoupons,
    handleErrorAppFreeCouponMaxLimit,
  } = useCoupon();

  const pickupInfo = LocalStorageManager.Instance.getObject<RedirectPickupInfo>(
    AppConfig.Instance.LocalStorageKey["pickupInfo"],
  );

  const appToken = LocalStorageManager.Instance.getObject<string>(
    AppConfig.Instance.LocalStorageKey["appToken"],
  );

  const handleReturn = useCallback(() => {
    if (pickupInfo && appToken) {
      const { redirectUrl } = pickupInfo;
      window.location.href = redirectUrl;
    } else {
      navigateToPage(PageRoutes.APP_COUPON);
    }
  }, []);

  const validateAppMultiCoupons = useCallback(
    (
      coupon: CouponExternal,
      appliedCoupons: AppliedCoupon[] | null,
    ): boolean => {
      const alreadyAppliedMultiCoupon = appliedCoupons?.find(
        (appliedCoupon) =>
          appliedCoupon.code === coupon.code &&
          appliedCoupon.menuCode === coupon.menu!.code &&
          coupon.kind === CouponType.AppMulti,
      );
      if (alreadyAppliedMultiCoupon) {
        dispatch(
          appActions.showModal.started({
            title:
              "すでに同一のクーポンがあります。\nカート内で該当のメニューの\n数量を変更してください。",
            nextButtonLabel: "閉じる",
            handleNext: () => {
              dispatch(appActions.closeModal());
            },
          }),
        );
        return false;
      }
      return true;
    },
    [],
  );

  const validateAppCoupons = useCallback(
    (
      coupon: CouponExternal,
      appliedCoupons: AppliedCoupon[] | null,
    ): boolean => {
      const alreadyAppliedAppCoupon = appliedCoupons?.find(
        (appliedCoupon) =>
          appliedCoupon.code.substring(0, 5) === coupon.code.substring(0, 5) &&
          coupon.kind === CouponType.App,
      );
      if (alreadyAppliedAppCoupon && !coupon.isFreeCoupon) {
        dispatch(
          appActions.showModal.started({
            title: "このクーポンは一枚のみお使いいただけます。",
            nextButtonLabel: "閉じる",
            handleNext: () => {
              dispatch(appActions.closeModal());
            },
          }),
        );
        return false;
      }
      return true;
    },
    [],
  );

  const handleUseCoupon = useCallback(
    (coupon: CouponExternal, quantity: number) => {
      if (!pickupInfo) return;

      const { storeId, pickupDate, storeShortestTimeUntilPickup, redirectUrl } =
        pickupInfo;

      // Handle total coupon
      if (coupon.isTotalCoupon) {
        applyCoupon(
          storeId,
          pickupDate,
          storeShortestTimeUntilPickup,
          [coupon.code],
          true,
          undefined,
          () => {
            setSelectedCouponGroup(null);
            window.location.href = redirectUrl;
          },
        );
        return;
      }

      // Validate multi and single use app coupons
      if (
        !validateAppMultiCoupons(coupon, appliedCoupons) ||
        !validateAppCoupons(coupon, appliedCoupons)
      ) {
        return;
      }

      // Free coupon usage limit check
      const usedFreeCouponsCount =
        (appliedCoupons?.filter((c) => c.isFreeCoupon).length ?? 0) + 1;

      if (usedFreeCouponsCount > remainingSlot) {
        handleErrorAppFreeCouponMaxLimit();
        return;
      }

      // Prepare coupon menu data
      const menuExternalCoupon: MenuExternalCoupon[] = [
        {
          ...coupon.menu!,
          couponCode: coupon.code,
          quantity,
          isFreeCoupon: coupon.isFreeCoupon,
        },
      ];

      applyCoupon(
        storeId,
        pickupDate,
        storeShortestTimeUntilPickup,
        [coupon.code],
        true,
        menuExternalCoupon,
        () => {
          setSelectedCouponGroup(null);
          window.location.href = redirectUrl;
        },
      );
    },
    [appliedCoupons, pickupInfo, applyCoupon],
  );

  const getQuantityOfAppliedCoupon = useCallback(
    (coupon: CouponExternal) => {
      const appliedQty =
        appliedCoupons?.filter(
          (appliedCoupon) => appliedCoupon.code === coupon.code,
        )?.length ?? 0;
      return appliedQty;
    },
    [appliedCoupons],
  );

  const isUsingGroupCoupon = useCallback(
    (couponGroup: CouponGroupExternal): boolean => {
      return (
        appliedCoupons?.some((appliedCoupon) =>
          couponGroup.parentCode.startsWith(appliedCoupon.code.substring(0, 5)),
        ) ?? false
      );
    },
    [appliedCoupons],
  );

  const handleOpenCouponGroupModal = useCallback(
    (couponGroup: CouponGroupExternal) => {
      setSelectedCouponGroup(couponGroup);
    },
    [],
  );

  const handleOpenModal = useCallback(
    (coupon: CouponExternal) => {
      if (coupon.isTotalCoupon) {
        // Apply total coupon directly
        if (pickupInfo) {
          const {
            storeId,
            pickupDate,
            storeShortestTimeUntilPickup,
            redirectUrl,
          } = pickupInfo;
          applyCoupon(
            storeId,
            pickupDate,
            storeShortestTimeUntilPickup,
            [coupon.code],
            true,
            undefined,
            () => {
              setSelectedCouponGroup(null);
              window.location.href = redirectUrl;
            },
          );
        }
        return;
      }
      handleUseCoupon(coupon, 1);
    },
    [pickupInfo, applyCoupon, handleUseCoupon],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedCouponGroup(null);
  }, []);

  // Validate Selected App Coupons
  const validateSelectedAppFreeCoupons = useCallback(
    (slot: number, redirectUrl: string) => {
      if (!selectedAppCoupons || selectedAppCoupons.length === 0) return;
      const freeCouponCodes = selectedAppCoupons
        .filter((coupon) => coupon.isFreeCoupon)
        .map((coupon) => coupon.code);
      const total =
        (appliedCoupons?.filter((coupon) => coupon.isFreeCoupon).length ?? 0) +
        freeCouponCodes.length;
      if (total > slot) {
        handleErrorAppFreeCouponMaxLimit(() => {
          // Clear selected app free coupons when max limit is reached
          dispatch(
            couponActions.removeSelectedAppCoupon.started({
              couponCodes: freeCouponCodes,
            }),
          );
          window.location.href = redirectUrl;
        });
        return;
      }
      window.location.href = redirectUrl;
    },
    [appliedCoupons, selectedAppCoupons, handleErrorAppFreeCouponMaxLimit],
  );

  useEffect(() => {
    if (!appToken) {
      dispatch(
        appActions.showModal.started({
          title: "アプリクーポンの取得に失敗しました",
          warningText:
            "アプリクーポンの取得に失敗しました。再度お試しください。",
          nextButtonLabel: "閉じる",
          handleNext: () => {
            LocalStorageManager.Instance.removeObject(
              AppConfig.Instance.LocalStorageKey["appToken"],
            );
            dispatch(appActions.closeModal());
            navigateToPage(PageRoutes.APP_COUPON);
          },
        }),
      );
      return;
    }
    if (pickupInfo) {
      const { storeId, pickupDate, redirectUrl } = pickupInfo;
      dispatch(
        couponActions.getCouponExternals.started({
          app_token: appToken,
          store_id: storeId,
          pickup_date: pickupDate,
          onError(error) {
            LocalStorageManager.Instance.removeObject(
              AppConfig.Instance.LocalStorageKey["appToken"],
            );
            navigateToPage(PageRoutes.APP_COUPON);
          },
          onSuccess({ remainingSlot }) {
            validateSelectedAppFreeCoupons(remainingSlot, redirectUrl);
          },
        }),
      );
    }
  }, []);

  return {
    couponExternalGroups,
    selectedCouponGroup,
    getQuantityOfAppliedCoupon,
    handleUseCoupon,
    handleReturn,
    handleOpenModal,
    handleCloseModal,
    handleOpenCouponGroupModal,
    isUsingGroupCoupon,
  };
};
